/**
 * Admin Role Hook
 * Check if the current user has admin role in the user_roles table
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface AdminRoleResult {
    isAdmin: boolean;
    isLoading: boolean;
    error: Error | null;
}

/**
 * Hook to check if the current user has an admin role
 * Uses the has_role RPC function to check the user_roles table
 */
export const useAdminRole = (): AdminRoleResult => {
    const { user } = useAuth();

    const { data, isLoading, error } = useQuery({
        queryKey: ["admin-role", user?.id],
        queryFn: async () => {
            if (!user) return false;

            const { data, error } = await supabase.rpc("has_role", {
                _user_id: user.id,
                _role: "admin",
            });

            if (error) {
                console.error("Error checking admin role:", error);
                throw error;
            }

            return data || false;
        },
        enabled: !!user,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    });

    return {
        isAdmin: data ?? false,
        isLoading,
        error: error as Error | null,
    };
};
